export const getPerformancePersonal = async function (qwSysUserId) {
  let personalInfo = sessionStorage.getItem('personalInfo') ? JSON.parse(sessionStorage.getItem('personalInfo')) : {}
  const cached = personalInfo[qwSysUserId]
  if (cached && cached.roleInfos) return cached

  const res = await $http.get('getPerformancePersonal', {
    params: {
      qwSysUserId
    }
  });
  if (res.code != 200 || !res.data) {
    const err = { code: res.code, msg: res.msg || res.message || '获取人员信息失败' }
    return Promise.reject(err)
  }
  personalInfo[qwSysUserId] = res.data
  sessionStorage.setItem('personalInfo', JSON.stringify(personalInfo))
  return personalInfo[qwSysUserId]
}

export const setPerformancePersonal = function (qwSysUserId, data) {
  let personalInfo = sessionStorage.getItem('personalInfo') ? JSON.parse(sessionStorage.getItem('personalInfo')) : {}
  personalInfo[qwSysUserId] = {...personalInfo[qwSysUserId], ...data}
  sessionStorage.setItem('personalInfo', JSON.stringify(personalInfo))

}

/** 从 sessionStorage 读取 CRM 人员信息缓存 */
export function getCachedPersonalInfo(qwSysUserId) {
  if (!qwSysUserId) return null
  try {
    const personalInfo = sessionStorage.getItem('personalInfo')
      ? JSON.parse(sessionStorage.getItem('personalInfo'))
      : {}
    return personalInfo[qwSysUserId] || null
  } catch (e) {
    return null
  }
}

/** getStaffInfo.role：最高职级 */
export const HIGHEST_LEVEL_ROLE = 'HIGHEST_LEVEL'

export function isHighestLevelRole(role) {
  return role === HIGHEST_LEVEL_ROLE
}

/**
 * 最高职级（getStaffInfo.role === HIGHEST_LEVEL）任务/日程同时看大宗周度与鸡窝日拜访
 */
export function canSeeBothTaskBusinessTypes(staffInfo) {
  return isHighestLevelRole(staffInfo && staffInfo.role)
}

/** 鸡蛋职位暂不展示任务日程与今日拜访任务；鸡窝业务员已放开 */
export function shouldHideTaskSchedule(personalInfo) {
  if (!personalInfo) return false
  const role = personalInfo.examineRole || ''
  const categoryId = Number(personalInfo.categoryId)

  if (categoryId === 9 || role.includes('鸡蛋')) return true

  return false
}

/** CRM 首页大宗/鸡窝切换后缓存在 sessionStorage 的 salesmanType（1 大宗 2 鸡窝） */
export function getCachedPersonalSalesmanType(qwSysUserId) {
  if (!qwSysUserId) return null
  try {
    const personalInfo = sessionStorage.getItem('personalInfo')
      ? JSON.parse(sessionStorage.getItem('personalInfo'))
      : {}
    const cached = personalInfo[qwSysUserId]
    if (cached && cached.salesmanType != null && cached.salesmanType !== '') {
      const n = Number(cached.salesmanType)
      return n === 1 || n === 2 ? n : null
    }
  } catch (e) {
    /* ignore */
  }
  return null
}

const salesmanInfoCacheKey = 'salesmanInfo'

function readSalesmanInfoCache() {
  try {
    return sessionStorage.getItem(salesmanInfoCacheKey)
      ? JSON.parse(sessionStorage.getItem(salesmanInfoCacheKey))
      : {}
  } catch (e) {
    return {}
  }
}

function cacheKeyForSalesmanQuery({ qwUserId, jobNumber, sysUserId } = {}) {
  return [qwUserId || '', jobNumber || '', sysUserId != null ? String(sysUserId) : ''].join('|')
}

/** 规范化业务类型：1 大宗 2 鸡窝 */
export function normalizeBusinessType(value) {
  const n = Number(value)
  return n === 1 || n === 2 ? n : null
}

/** 读取已缓存的业务员信息（querySalesmanInfo） */
export function getCachedSalesmanInfo(params = {}) {
  const cache = readSalesmanInfoCache()
  const key = cacheKeyForSalesmanQuery(params)
  if (key !== '||' && cache[key]) return cache[key]
  if (params.qwUserId) {
    const hit = Object.values(cache).find(
      (item) => item && item.qwUserId === params.qwUserId
    )
    if (hit) return hit
  }
  return null
}

/** 读取已缓存的 businessType（1 大宗 2 鸡窝） */
export function getCachedSalesmanBusinessType(params = {}) {
  const info = getCachedSalesmanInfo(params)
  return info ? normalizeBusinessType(info.businessType) : null
}

/**
 * 查询业务员信息（任务大宗/鸡窝区分）
 * POST /zuul/lbb-marketing/wap/dingDepartmentUser/querySalesmanInfo
 * @param {{ jobNumber?: string, qwUserId?: string, sysUserId?: number|string, force?: boolean }} params
 */
export async function querySalesmanInfo(params = {}) {
  const { jobNumber, qwUserId, sysUserId, force } = params
  if (!jobNumber && !qwUserId && (sysUserId == null || sysUserId === '')) {
    return null
  }
  const key = cacheKeyForSalesmanQuery({ jobNumber, qwUserId, sysUserId })
  const cache = readSalesmanInfoCache()
  if (!force && cache[key]) return cache[key]

  const body = {}
  if (jobNumber) body.jobNumber = jobNumber
  if (qwUserId) body.qwUserId = qwUserId
  if (sysUserId != null && sysUserId !== '') body.sysUserId = Number(sysUserId)

  const res = await $http.post('querySalesmanInfo', body)
  if (res.code != 200 || !res.data) {
    return null
  }
  cache[key] = res.data
  if (res.data.qwUserId) {
    cache[cacheKeyForSalesmanQuery({ qwUserId: res.data.qwUserId })] = res.data
  }
  sessionStorage.setItem(salesmanInfoCacheKey, JSON.stringify(cache))
  return res.data
}

/**
 * 解析任务场景下的业务类型（1 大宗 2 鸡窝）
 * 以 querySalesmanInfo.businessType 为准，不用 CRM 头部 salesmanType。
 * 优先级：显式 businessType > 接口缓存 > staffInfo.businessType > 路由 > CRM salesmanType 兜底
 */
export function resolveTaskBusinessType({
  explicit,
  routeQuery,
  qwUserId,
  staffInfo,
} = {}) {
  const fromExplicit = normalizeBusinessType(explicit)
  if (fromExplicit) return fromExplicit

  const fromApi = getCachedSalesmanBusinessType({ qwUserId })
  if (fromApi) return fromApi

  if (staffInfo) {
    const fromStaffApi = normalizeBusinessType(staffInfo.businessType)
    if (fromStaffApi) return fromStaffApi
  }

  const q =
    routeQuery &&
    (routeQuery.businessType != null && routeQuery.businessType !== ''
      ? routeQuery.businessType
      : routeQuery.salesmanType)
  const fromRoute = normalizeBusinessType(q)
  if (fromRoute) return fromRoute

  const fromCrm = getCachedPersonalSalesmanType(qwUserId)
  if (fromCrm) return fromCrm

  if (staffInfo) {
    const fromStaff = normalizeBusinessType(staffInfo.salesmanType)
    if (fromStaff) return fromStaff
  }
  return null
}

let options = {}

export const getSelectOptions = async function (name) {
  let option = options[name]
  if (option) return option
  else {
    try {
      const res = await $http.get('queryListByCode', {
        params: {code: name, isCancel: 1},
        isCancel: 1
        }
      )
      console.log({res})
      options[name] = res.data
    } catch (e) {
      options[name] = []
    }
    return options[name]
  }
}

/** 金融白条逾期标签 channel code（融资逾期） */
export const WHITE_STRIP_OVERDUE_CHANNEL_CODE = 'shy209138';
/** 核心养殖户 */
export const CORE_FARMER_CHANNEL_CODE = 'CORE_FARMER';
/** 实证客户 */
export const VERIFY_CUSTOMER_CHANNEL_CODE = 'VERIFY_CUSTOMER';
/** KA客户 */
export const KA_CUSTOMER_CHANNEL_CODE = 'shy691638';

export function hasCustomerChannelCode(customer, channelCode) {
  if (!customer || !channelCode) return false;
  const list = customer.userChannelList;
  if (!Array.isArray(list)) return false;
  return list.some((item) => item && item.code === channelCode);
}

export function isWhiteStripOverdueCustomer(customer) {
  return hasCustomerChannelCode(customer, WHITE_STRIP_OVERDUE_CHANNEL_CODE);
}

export function isCoreFarmerCustomer(customer) {
  return hasCustomerChannelCode(customer, CORE_FARMER_CHANNEL_CODE);
}

export function isVerifyCustomer(customer) {
  return hasCustomerChannelCode(customer, VERIFY_CUSTOMER_CHANNEL_CODE);
}

export function isKaCustomer(customer) {
  return hasCustomerChannelCode(customer, KA_CUSTOMER_CHANNEL_CODE);
}
